import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ToasterService } from '@purespectrum1/ui/toaster-service';
import { ModalService } from '@purespectrum1/ui/modal';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { CommonService } from '../../shared/services/common-service/common-service';
import {
  CategoriesInterface,
  CountryInterface,
  GetPSIDResponse,
  LanguageInterface,
  PSIDObj,
  UploadPSIDResponse,
} from '../../shared/interfaces/create-surveys.interface';
import {
  Qualification,
  QualificationPayload,
} from '../../shared/interfaces/qualification.interface';
import { QualificationsService } from '../services/qualification/qualifications.service';
import { Constants } from '../../shared/constants/create-survey.constants';
import { notifyMessage } from '../../shared/constants/notify-message';
import { SurveyService } from '../../shared/services/survey/survey.service';
import { BuyerApiService } from '../../shared/services/buyer-api/survey.service';
import { SURVEY_STATUS } from '../../shared/constants/survey-status';
import { ActivatedRoute, Router } from '@angular/router';
import {
  RecontactSurveyRequest,
  SurveyRequest,
  BuyerSurveyResponse,
} from '../../shared/interfaces/survey.interface';
import { PsidViewModalComponent } from './psid-view-modal/psid-view-modal.component';
import { TEMPLATE_FILE_PATHS } from '../constants/template-file-path';
import { ConfirmationModalComponent } from '../../shared/ui/confirmation-modal/confirmation-modal.component';
import { SurveyMetaDataService } from '../../shared/services/survey-meta-data/survey-meta-data.service';
import { debounceTime, filter, switchMap, tap } from 'rxjs/operators';
import { PricingService } from '../services/pricing/pricing.service';
import { ChurnZeroConsts, ChurnZeroService } from '@purespectrum1/ui/tracking';
import { AuthState } from '../../shared/interfaces/auth.interface';
import { AuthService } from '../../shared/services/auth/auth.service';
import { BillingRulesService } from '../../shared/services/billing-rules/billing-rules-service';
import { BillingNumberRules } from '../../shared/interfaces/billing-rules.interfaces';

@Component({
  selector: 'ps-ui-recontact-survey',
  templateUrl: './recontact-survey.component.html',
  styleUrls: ['./recontact-survey.component.css'],
})
export class RecontactSurveyComponent implements OnInit, OnDestroy {
  @Output() closeSurvey = new EventEmitter<boolean>();
  @Input() set auth(data: AuthState) {
    this._auth.setState(data);
  }
  private _subscriptions: Subscription = new Subscription();
  private _surveyDetails!: BuyerSurveyResponse;
  private _surveyId: number = 0;
  private _cpi: number = 0;
  private _currencySymbol: string = '';

  public form!: FormGroup;
  public countrieInfo: CountryInterface[] = [];
  public selectedFirst: boolean = false;
  public countries: CountryInterface[] = [];
  public languages: LanguageInterface[] = [];
  public isEditMode: boolean = false;
  public categories: CategoriesInterface[] = [];
  public selectedCountriesLength: number = 0;
  public isSurveyCreated = false;
  public qualifications: Qualification[] = [];
  public editLabel: string = 'Save changes';
  public numberOfCompletes: number = 0;
  public refList: PSIDObj[] = [];
  public psidFilePath?: string;
  public psidTemplatePath: string =
    TEMPLATE_FILE_PATHS.PSID_TEMPLATE_GENERAL_LINK;
  public billingRule$: Observable<BillingNumberRules>;
  readonly QUALIFICATIONS_RENDER_TYPE = Constants.QUALIFICATIONS_RENDER_TYPE;

  constructor(
    private _fb: FormBuilder,
    private _toastr: ToasterService,
    private _commonService: CommonService,
    private _qualificationService: QualificationsService,
    private _surveyService: SurveyService,
    private _surveyMetaDataService: SurveyMetaDataService,
    private _buyerApiService: BuyerApiService,
    private _router: Router,
    private _modal: ModalService,
    private _route: ActivatedRoute,
    private _churnZeroService: ChurnZeroService,
    private _pricingService: PricingService,
    private _auth: AuthService,
    private _billingRulesService: BillingRulesService
  ) {}

  ngOnInit() {
    this._initForm();
    this._getCountry();
    const param = this._route.params.subscribe((params) => {
      if (params['id']) {
        this._editRecontactSurvey(params['id']);
      } else {
        this.selectedFirst = true;
        this.isEditMode = false;
        if (this.countries.length) {
          this.countries[0].selected = true;
          this.selectRecontactCountry(this.countries[0]);
        }
        this._fetchBillingRules();
      }
    });
    this._subscriptions.add(param);

    const formSubscription = combineLatest([
      this.form.controls.selectedCountry.valueChanges,
      this.form.controls.selectedLanguage.valueChanges,
      this.form.controls.incidence.valueChanges,
      this.form.controls.lengthOfSurvey.valueChanges,
      this.form.controls.fieldTime.valueChanges,
    ])
      .pipe(
        debounceTime(1000),
        filter(
          ([, , incidence, lengthOfSurvey, fieldTime]) =>
            !!fieldTime && !!incidence && !!lengthOfSurvey
        ),
        switchMap(
          ([
            selectedCountry,
            selectedLanguage,
            incidence,
            lengthOfSurvey,
            fieldTime,
          ]) => {
            return this._pricingService.getEligibleSuppliers(
              selectedCountry.short_Code,
              selectedLanguage.short_code,
              lengthOfSurvey,
              incidence,
              '0',
              false,
              fieldTime
            );
          }
        )
      )
      .subscribe((data) => {
        this._cpi = data.CPI;
        this._currencySymbol = data.currencyFx.symbol;
        this._calculateTotalCost();
      });
    this._subscriptions.add(formSubscription);
  }

  private _editRecontactSurvey(id: number) {
    this.isEditMode = true;
    this._surveyId = id;
    this._buyerApiService.getSurveyById(id).subscribe((response) => {
      if (response.ps_survey_status === SURVEY_STATUS.DRAFT) {
        this.editLabel = 'Launch';
      }
      this._surveyDetails = response;
      this.numberOfCompletes = this._surveyDetails.completes_required;
      const selectedCountrie = this._getCountrieInfo();
      this.psidFilePath = response.psidFilePath;
      this.form.patchValue({
        surveyTitle: this._surveyDetails.survey_title,
        lengthOfSurvey: this._surveyDetails.expected_loi,
        incidence: this._surveyDetails.expected_ir,
        fieldTime: this._surveyDetails.field_time,
        surveyEntryLink: this._surveyDetails.live_url,
        poNumber: this._surveyDetails.billing_id,
        testEntryLink: this._surveyDetails.test_url,
        uniqueLinks: this._surveyDetails.uniqueLinks,
        ref_list: this.refList,
      });
      this._currencySymbol = this._surveyDetails.currency_symbol;
      this.selectRecontactCountry(selectedCountrie);
      this._updateHeaderInfo(
        response.total_cost.launch,
        response.field_time,
        response.ps_survey_status,
        response.expected_loi,
        response.expected_ir,
        response.incl_excl,
        response.is_pure_price_rate_card_enable
      );
      this._fetchBillingRules();
    });
  }

  private _getCountrieInfo() {
    this.countrieInfo = this.countries.filter((contrie) => {
      return contrie.short_Code == this._surveyDetails.locale.country_code;
    });
    return this.countrieInfo[0];
  }

  private _initForm() {
    this.form = this._fb.group({
      surveyTitle: [null, [Validators.required]],
      selectedCountry: [null, [Validators.required]],
      selectedLanguage: [null, [Validators.required]],
      lengthOfSurvey: [null, [Validators.required]],
      incidence: [null, [Validators.required]],
      fieldTime: [null, [Validators.required]],
      uniqueLinks: [false, [Validators.required]],
      surveyEntryLink: [null, [Validators.required]],
      poNumber: [null],
      testEntryLink: [null],
    });
  }

  private _getQualification() {
    const countryCode = this.form.value.selectedCountry?.short_Code;
    const languageCode = this.form.value.selectedLanguage?.transalte_code;
    const qualificationsSub = this._qualificationService
      .getUIQualifications(
        countryCode,
        languageCode,
        this.selectedCountriesLength,
        true
      )
      .subscribe(
        (qualifications: Qualification[]) => {
          this.qualifications = qualifications;
          this.qualifications.forEach((qualification) => {
            qualification.data = qualification.data.map((item) => {
              item.selected = true;
              return item;
            });
          });
          if (this._surveyDetails?.qualifications) {
            this.populateCoreQualifications(
              this.qualifications,
              this._surveyDetails.qualifications
            );
          }
        },
        (err) => {
          this._toastr.error(err.error.msg);
        }
      );
    this._subscriptions.add(qualificationsSub);
  }

  populateCoreQualifications(
    qualifications: Qualification[],
    surveyQualifications: any[]
  ) {
    return qualifications.map((qualification) => {
      const surveyQualification = surveyQualifications.find(
        (_surveyQualification) => {
          return (
            _surveyQualification.qualification_code ===
            qualification.qualification_code
          );
        }
      );
      if (surveyQualification) {
        qualification.data = this._mapOptionSelected(
          qualification,
          surveyQualification
        );
      }
      return qualification;
    });
  }
  private _mapOptionSelected(
    qualification: Qualification,
    surveyQualification: any | undefined
  ) {
    return qualification.data.map((option) => {
      if (
        surveyQualification?.condition_codes?.includes(option.id.toString())
      ) {
        option.selected = true;
      } else {
        option.selected = false;
      }
      return option;
    });
  }

  private _getCountry() {
    const countries = this._commonService.getCountries().subscribe(
      (countriesData) => {
        this.countries = countriesData;
      },
      (err) => {
        this._toastr.error(err.error.msg);
      }
    );
    this._subscriptions.add(countries);
  }

  selectRecontactCountry(country: CountryInterface) {
    this.form.patchValue({ selectedCountry: country });
    this.selectedCountriesLength = 1;
    this.setLanguageForRecontactCountry(this.form.value.selectedCountry);
  }

  setRecontactLanguage(language: LanguageInterface) {
    this.form.patchValue({ selectedLanguage: language });
  }

  setLanguageForRecontactCountry(country: CountryInterface) {
    this.languages = country.lang;
    this.setRecontactLanguage(this.languages[0]);
    this._getQualification();
  }

  private _calculateTotalCost() {
    if (this.refList.length > 0) {
      const total = this.refList.length * this._cpi;
      this._updateHeaderInfo(total, 0, 0, 0, 0, 0, false);
    }
  }

  get isMandatoryBillingNumberEnabled() {
    if (
      this._auth.userType === 'operator' &&
      !this._auth.isLoggedInAsServiceBuyer()
    ) {
      return this._auth.allBuyerConfigs.enableMandatoryBillingNumber.includes(
        this._surveyDetails.buyer_company
      );
    }
    return this._auth.buyerConfig?.enableMandatoryBillingNumber;
  }

  private _updateHeaderInfo(
    totalCost: number,
    fieldTime: number,
    surveyStatus: number,
    expectedLoi: number,
    expectedIr: number,
    incl_excl: number,
    is_pure_price_rate_card_enable: boolean
  ) {
    this._surveyMetaDataService.sendSurveyHeaderData({
      total_cost: totalCost,
      surveyLastSaved: 0,
      surveyFieldTime: fieldTime,
      surveyId: this._surveyId,
      surveyStatus: surveyStatus,
      expected_loi: expectedLoi,
      expected_ir: expectedIr,
      incl_excl: incl_excl,
      currencySymbol: this._currencySymbol,
      is_pure_price_rate_card_enable,
    });
  }

  updateSurveyDetails() {
    this._validateAllFormFields(this.form);
    const payload = this._mapRecontactPayload();
    const sId = this._surveyDetails.ps_survey_id;
    if (
      this._surveyDetails.ps_survey_status === SURVEY_STATUS.DRAFT &&
      payload
    ) {
      payload.ps_survey_status = SURVEY_STATUS.LIVE;
    }
    const updateSurvey = this._buyerApiService
      .updateSurvey(payload as any, sId as any)
      .subscribe(
        (surveyCreatedResponse) => {
          if (Object.keys(surveyCreatedResponse).length) {
            this.isSurveyCreated = true;
          }
          this._churnZeroService.sendEvent(
            ChurnZeroConsts.events.draftSurveyUpdated,
            ChurnZeroConsts.eventDescriptions.draftSurveyUpdated.replace(
              ':id',
              sId + ''
            )
          );
          if (this._surveyDetails.ps_survey_status === SURVEY_STATUS.DRAFT) {
            this._redirectToUrl(
              false,
              surveyCreatedResponse.test_ps_survey_entry_link
            );
          } else {
            this._router.navigate([`dashboard/${this._surveyId}`]);
          }
          this._subscriptions.add(updateSurvey);
          this._toastr.success(
            notifyMessage.successMessage.RECONTACT_SURVEY.UPDATED
          );
        },
        (err) => {
          this._toastr.error(err.error.ps_api_response_message);
        }
      );
  }

  saveSurveyDetails(launchSurvey: boolean, testLink: boolean = false) {
    this._validateAllFormFields(this.form);
    this._validateMandatoryBillingNumber();
    const payload = this._mapRecontactPayload();
    const addSurvey = this._buyerApiService
      .addSurvey(payload as SurveyRequest)
      .subscribe(
        async (surveyCreatedResponse) => {
          if (Object.keys(surveyCreatedResponse).length) {
            this.isSurveyCreated = true;
            const surveyId = surveyCreatedResponse.ps_survey_id;
            try {
              if (launchSurvey) {
                await this._buyerApiService
                  .updateSurveyStatus(surveyId, {
                    ps_survey_status: SURVEY_STATUS.LIVE,
                  })
                  .toPromise();
              }
              this._redirectToUrl(
                testLink,
                surveyCreatedResponse.test_ps_survey_entry_link
              );
            } catch (e: any) {
              this._toastr.error(e.error.ps_api_response_message);
            }
          }
        },
        (err) => {
          this._toastr.error(err.error.ps_api_response_message);
        }
      );

    this._subscriptions.add(addSurvey);
  }

  private _validateAllFormFields(formGroup: FormGroup) {
    const fieldsWithError: string[] = [];
    Object.keys(formGroup.controls).forEach((field) => {
      const control = formGroup.get(field);
      if (control instanceof FormControl && control.status === 'INVALID') {
        fieldsWithError.push(field);
      }
    });

    if (fieldsWithError.length) {
      throw this._toastr.error(
        `Error: Please enter ${fieldsWithError[0]} to continue`
      );
    } else if (!this.numberOfCompletes) {
      throw this._toastr.error(
        notifyMessage.errorMessage.RECONTACT_RESPONDENT_LIST
      );
    } else if (!this.refList.length && !this.isEditMode) {
      throw this._toastr.error(notifyMessage.errorMessage.UPLOAD_PSID_FILE);
    }
  }

  private _validateMandatoryBillingNumber() {
    if (!this.isMandatoryBillingNumberEnabled) {
      return;
    }
    if (this.form.value.poNumber) {
      return;
    }
    throw this._toastr.error(
      notifyMessage.errorMessage.BILLING_NUMBER_MANDATORY
    );
  }

  private _redirectToUrl(testLink: boolean, testPsSurveyEntryLink: string) {
    if (testLink) {
      window.open(testPsSurveyEntryLink, '_blank');
    } else {
      this._router.navigate(['/dashboard']);
    }
  }

  private _mapRecontactPayload() {
    if (!this.refList.length && !this.isEditMode) {
      return this._toastr.error('Please upload respondent list before ');
    }
    let payload: RecontactSurveyRequest = {
      survey_title: this.form.value.surveyTitle,
      survey_category_code: 234,
      survey_localization: `${this.form.value.selectedLanguage?.transalte_code}_${this.form.value.selectedCountry.short_Code}`,
      completes_required: this.numberOfCompletes,
      expected_ir: this.form.value.incidence,
      expected_loi: this.form.value.lengthOfSurvey,
      field_time: this.form.value.fieldTime,
      live_url: this.form.value.surveyEntryLink,
      incl_excl: 1,
      uniqueLinks: this.form.value.uniqueLinks,
      psidFilePath: this.psidFilePath,
      source: 'platform',
    };

    if (this.refList.length) {
      payload.ref_list = this.refList;
    }

    if (this.form.value.testEntryLink) {
      payload.test_url = this.form.value.testEntryLink;
    }

    if (this.form.value.poNumber) {
      payload.billing_id = this.form.value.poNumber;
    }

    if (this.isEditMode && this._surveyDetails.price_override) {
      payload.offer_price = Number(this._surveyDetails.price_override);
    }

    const qualifications = this._mapQualificationsForRecontact();

    if (qualifications.length) {
      payload = { ...payload, ...{ qualifications: qualifications } };
    }
    return payload;
  }

  setQualification(qualification: Qualification) {
    const selectedOptions = qualification.data.filter((data) => data.selected);
    if (this.isSurveyCreated && !selectedOptions.length) {
      this._toastr.error(
        Constants.CREATE_SURVEY_ERROR_MESSAGE.ENTER_CRITERIA_SELECTION.replace(
          '@qualification',
          qualification.qualification_name
        )
      );
      return;
    }
    const coreIndex = this.qualifications.findIndex(
      (qual) => qual.qualification_code === qualification.qualification_code
    );

    if (coreIndex >= 0) {
      this.qualifications[coreIndex].data = qualification.data;
    }
  }

  private _mapQualificationsForRecontact() {
    const qualifications: QualificationPayload[] = [];
    this.qualifications.forEach((qualification) => {
      if (
        qualification.render_type ===
        this.QUALIFICATIONS_RENDER_TYPE.MULTIPUNCH_DROPDOWN
      ) {
        const selectedOptions = qualification.data.filter(
          (data) => data.selected
        );

        if (selectedOptions.length !== qualification.data.length) {
          qualifications.push({
            qualification_name: qualification.qualification_name,
            qualification_code: qualification.qualification_code,
            condition_codes: qualification.data
              .filter((code) => code.selected)
              .map((code) => `${code.id}`),
          });
        }
      }

      if (
        qualification.render_type === this.QUALIFICATIONS_RENDER_TYPE.RANGE &&
        qualification.data[0].minValue &&
        qualification.data[0].maxValue
      ) {
        qualifications.push({
          qualification_name: qualification.qualification_name,
          qualification_code: qualification.qualification_code,
          range_sets: [
            {
              from: Number(qualification.data[0].minValue),
              to: Number(qualification.data[0].maxValue),
              units: this._commonService.getUnit(
                qualification.qualification_code
              ),
            },
          ],
        });
      }
    });
    return qualifications;
  }

  uploadRespondentFile(event: Event) {
    const target = event.target as HTMLInputElement;
    const file: File = (target.files as FileList)[0];
    const isValidFile = this._validateFileExtention(file);
    if (isValidFile) {
      let fdata = new FormData();
      fdata.append('psid_ref_file', file);
      fdata.append('hasRedirectUrl', this.form.value.uniqueLinks.toString());
      const psidData = this._surveyService.uploadPSIDFile(fdata).subscribe(
        (response: UploadPSIDResponse) => {
          if (!response.data.uniq_psid) {
            this._toastr.error(notifyMessage.errorMessage.INVALID_PSID_FILE);
          }
          this.numberOfCompletes = response.data.total_psid;
          this.refList = response.data.uniq_psids;
          this.psidFilePath = response.data.filePath;
          if (this.form.value.uniqueLinks) {
            this.form.patchValue({ surveyEntryLink: this.refList[0].url });
          }

          if (response.data.skipped_psid == 0) {
            this._toastr.success(
              `${response.data.uniq_psid} out of ${response.data.uploaded_psid} psid successfully uploaded.`
            );
          } else {
            this._toastr.success(
              `${response.data.uniq_psid} out of ${response.data.uploaded_psid} psid successfully uploaded. ${response.data.skipped_psid} psid skipped`
            );
          }

          this._calculateTotalCost();
        },
        (err: any) => {
          this._toastr.error(err.error.msg);
        }
      );
      this._subscriptions.add(psidData);
    }
  }

  private _validateFileExtention(file: File) {
    const validExt = ['.csv'];
    if (!file) {
      this._toastr.error(notifyMessage.errorMessage.FILE_UPLOAD_MISSING);
      return false;
    }
    const fileExt = file.name.substring(file.name.lastIndexOf('.'));
    if (validExt.indexOf(fileExt) < 0) {
      this._toastr.error(notifyMessage.errorMessage.FILE_UPLOAD_MISSING);
      return false;
    }
    return true;
  }

  clearRespondentList() {
    this.numberOfCompletes = 0;
    this.refList = [];
    this.psidFilePath = undefined;
    this._surveyDetails.psidFilePath = undefined;
    this._surveyDetails.hasPSIDRefList = false;
    this._toastr.success(notifyMessage.successMessage.CLEAR_RESPONDENT_LIST);
  }

  viewLatestUpload() {
    const latestUpload = this._surveyService
      .getPSIDList(this.psidFilePath, this.form.value.uniqueLinks)
      .subscribe(
        (response: GetPSIDResponse) => {
          const psidArr: string[] = [];
          response.data.psids.forEach((psidObj) => {
            psidArr.push(psidObj.psid);
            if (psidObj.url) {
              psidArr.push(psidObj.url);
            }
          });
          this._modal.open(PsidViewModalComponent, {
            data: psidArr,
            width: '100%',
            height: '80%',
          });
        },
        ({
          data: { message = notifyMessage.errorMessage.SERVER_ERROR } = {},
        } = {}) => {
          this._toastr.error(message);
        }
      );
    this._subscriptions.add(latestUpload);
  }

  public clearUploadedRefList() {
    if (this.form.value.uniqueLinks === true) {
      this.psidTemplatePath = TEMPLATE_FILE_PATHS.PSID_TEMPLATE_UNIQUE_LINK;
    } else {
      this.psidTemplatePath = TEMPLATE_FILE_PATHS.PSID_TEMPLATE_GENERAL_LINK;
    }
    if (this.psidFilePath) {
      const modalRef = this._modal.open(ConfirmationModalComponent, {
        maxWidth: '450px',
        data: {
          text: Constants.RECONTACT_SURVEY.MODAL_TEXT.LINK_TOGGLE_CONFIRMATION,
        },
      });
      modalRef.onClose$.subscribe((msg) => {
        if (msg === 'ok') {
          this.numberOfCompletes = 0;
          this.refList = [];
          this.psidFilePath = undefined;
        } else {
          this.form.controls.uniqueLinks.setValue(!this.form.value.uniqueLinks);
        }
      });
    }
  }

  private _fetchBillingRules() {
    const buyerCompanyId = this._surveyDetails
      ? this._surveyDetails.buyer_company
      : 0;
    this.billingRule$ = this._billingRulesService
      .fetchRules(buyerCompanyId)
      .pipe(
        tap((billingRule) => {
          if (!this._surveyDetails || !this._surveyDetails.billing_id) {
            const billingId = billingRule.autoGenerate
              ? billingRule.example
              : '';
            this.form.patchValue({
              poNumber: billingId,
            });
          }
        })
      );
  }

  ngOnDestroy() {
    this._subscriptions.unsubscribe();
  }

  goToDashboard() {
    this.closeSurvey.emit();
  }
}
